/**
 * GraphQL Operations (Queries and Mutations)
 * 
 * Based on the Multihub GraphQL API structure.
 */

// ============================================================================
// Property Query Builder
// ============================================================================

/**
 * Inline media snapshot selected alongside every image field as `<field>_media`.
 * Supplies alt text (for alt linking) and identity (to disambiguate same-URL picks).
 */
const IMAGE_MEDIA_SNAPSHOT = '{ alt_text public_id storage_key }';
const VIDEO_MEDIA_SNAPSHOT = '{ public_id storage_key thumbnail_key size_bytes classification title description }';

/**
 * Build a complete property query with a specific property ID.
 * The API uses direct string interpolation rather than GraphQL variables.
 */
export function buildPropertyQuery(propertyId: string): string {
  return `
    {
      properties(propertyId:"${propertyId}"){
        multihub_agency_id
        multihub_company_id
        multihub_property_id
        property_external_id
        property_zillow_url
        property_apartments_url
        property_google_business_url
        property_linkedin_url
        property_hubspot_id
        property_crm_software
        property_pm_software
        property_pm_info
        property_status
        property_slug
        property_name
        property_website_url
        property_domain
        property_address
        property_full_address
        property_address_1
        property_address_2
        property_address_city
        property_address_state
        property_address_zip_full
        property_address_zip
        property_address_country
        property_address_county
        property_neighborhood_address
        property_address_latitude
        property_address_longitude
        property_email
        property_phone
        property_call_tracking_phone
        property_company_name
        property_company_url
        agency_contacts
        property_type
        property_short_description
        property_long_description
        property_featured_image
        property_featured_image_media ${IMAGE_MEDIA_SNAPSHOT}
        property_featured_video
        property_featured_video_media ${VIDEO_MEDIA_SNAPSHOT}
        property_total_units
        property_year_built
        property_year_renovated
        property_walk_score
        property_transit_score
        property_business_hours_list
        property_current_occupancy
        property_admin_fee
        property_application_fee
        property_fees_deposit_info
        property_ispetfriendly
        property_allows_cats
        property_allows_dogs
        property_upfront_pet_fee
        property_pet_deposit
        property_monthly_pet_rent
        property_max_pets
        property_max_pet_weight_limit
        property_pet_restrictions
        property_pet_policy_description
        property_petpolicy_info
        property_online_leasing_url
        property_resident_portal_url
        property_apply_now_url
        property_community_amenities_list
        property_apartment_features_list
        property_primary_logo
        property_primary_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_secondary_logo
        property_secondary_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_icon
        property_icon_media ${IMAGE_MEDIA_SNAPSHOT}
        property_fav_icon
        property_fav_icon_media ${IMAGE_MEDIA_SNAPSHOT}
        property_mono_white_logo
        property_mono_white_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_mono_black_logo
        property_mono_black_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_company_icon
        property_company_icon_media ${IMAGE_MEDIA_SNAPSHOT}
        property_company_logo
        property_company_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_company_mono_black_logo
        property_company_mono_black_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_company_mono_white_logo
        property_company_mono_white_logo_media ${IMAGE_MEDIA_SNAPSHOT}
        property_primary_color
        property_secondary_color
        property_font_color
        property_color_4
        property_color_3
        property_color_5
        property_color_6
        property_color_7
        property_color_8
        property_bedrooms_range
        property_bedrooms_min
        property_bedrooms_max
        property_bathrooms_range
        property_bathrooms_min
        property_bathrooms_max
        property_sqft_range
        property_sqft_min
        property_sqft_max
        property_price_range
        property_price_min
        property_price_max
        property_available_units
        property_facebook_url
        property_instagram_url
        property_twitter_url
        property_google_place_url
        property_youtube_url
        property_yelp_url
        property_tiktok_url
        property_specialoffer_title
        property_specialoffer_description
        property_specialoffer_disclaimer
        property_home_hero_title
        property_home_romance_title
        property_home_amenities_blurb
        property_home_floorplans_blurb
        property_home_gallery_blurb
        property_home_neighborhood_blurb
        property_home_reviews_blurb
        property_neighborhood_blurb
        property_accessibility_blurb
        property_amenities_blurb
        property_applynow_blurb
        property_contactus_blurb
        property_faqs_blurb
        property_floorplans_blurb
        property_gallery_blurb
        property_interactivemap_blurb
        property_pets_blurb
        property_referafriend_blurb
        property_residentaccess_blurb
        property_reviews_blurb
        property_created_at
        property_scheduletour_blurb
        property_thankyou_blurb
        property_virtualtours_blurb
        property_copyright
        property_seo_title_home
        property_seo_description_home
        property_seo_title_floorplans
        property_seo_description_floorplans
        property_seo_title_amenities
        property_seo_description_amenities
        property_seo_title_gallery
        property_seo_description_gallery
        property_seo_title_virtualtours
        property_seo_description_virtualtours
        property_seo_title_interactivemap
        property_seo_description_interactivemap
        property_seo_title_neighborhood
        property_seo_description_neighborhood
        property_seo_title_contact
        property_seo_description_contact
        property_seo_title_faqs
        property_seo_description_faqs
        property_seo_title_reviews
        property_seo_description_reviews
        property_seo_title_applynow
        property_seo_description_applynow
        property_seo_title_residentaccess
        property_seo_description_residentaccess
        property_seo_title_referafriend
        property_seo_description_referafriend
        property_seo_title_scheduletour
        property_seo_description_scheduletour
        property_seo_title_privacy
        property_seo_description_privacy
        property_seo_title_accessibility
        property_seo_description_accessibility
        property_seo_title_sitemap
        property_seo_description_sitemap
        property_seo_title_thankyou
        property_seo_description_thankyou
        property_seo_title_pets
        property_seo_description_pets
        property_updated_at
        amenities {
          id
          agency_id
          company_id
          property_id
          source
          external_id
          title
          amount
          description
          group
          is_featured
          amenity_image
          amenity_image_media ${IMAGE_MEDIA_SNAPSHOT}
          amenity_image_alt_text
          amenity_icon
          amenity_icon_media ${IMAGE_MEDIA_SNAPSHOT}
          amenity_icon_alt_text
        }
        photo_gallery {
          id
          source
          agency_id
          company_id
          property_id
          image
          image_media ${IMAGE_MEDIA_SNAPSHOT}
          title
          description
          link
          alt_text
          link_text
          source_page_urls
          original_url
        }
        neighborhood_pois {
          id
          agency_id
          company_id
          property_id
          source
          poi_name
          poi_type
          poi_address
          poi_short_description
          poi_long_description
          poi_featured_image
          poi_featured_image_media ${IMAGE_MEDIA_SNAPSHOT}
          poi_business_url
          poi_directions_map_url
          poi_item_slug
          poi_latitude
          poi_longitude
          poi_miles_from_property
          poi_walking_minutes
          poi_biking_minutes
          poi_driving_minutes
          poi_business_phone
          poi_map_icon
          poi_map_icon_media ${IMAGE_MEDIA_SNAPSHOT}
          poi_google_place_id
          poi_day_night_filter
          poi_place_primary_type
        }
        office_hours {
          id
          source
          agency_id
          company_id
          property_id
          day
          open_time
          close_time
        }
        floorplans {
          id
          agency_id
          company_id
          property_id
          source
          floorplan_id
          floorplan_name
          floorplan_available_units
          floorplan_price_min
          floorplan_price_max
          floorplan_beds_min
          floorplan_beds_max
          floorplan_baths_min
          floorplan_sqft_min
          floorplan_sqft_max
          floorplan_image_url
          floorplan_image_url_media ${IMAGE_MEDIA_SNAPSHOT}
        }
        reviews {
          id
          source
          agency_id
          company_id
          property_id
          rating
          name
          text
          date
          title
        }
        virtual_tours {
          id
          agency_id
          company_id
          property_id
          source
          url_health_error
          virtual_tour_description
          virtual_tour_thumbnail
          virtual_tour_thumbnail_media ${IMAGE_MEDIA_SNAPSHOT}
          virtual_tour_title
          virtual_tour_type
          virtual_tour_url
        }
        special_offers {
          id
          title
          description
          disclaimer
          start_date
          end_date
          enabled
        }
        faqs {
          id
          question
          answer
          category
          source
        }
        lead_collectors {
          lead_collector_id
          lead_source
          name
          agency_id
          company_id
          property_id
        }
        hyperlocal_queries {
          agency_id
          company_id
          property_id
          source
          page_item_url
          search_query
          category
          h1_title
          h2_subtitle
          page_copy
          featured_image
          featured_image_media ${IMAGE_MEDIA_SNAPSHOT}
          blank_image
          blank_image_media ${IMAGE_MEDIA_SNAPSHOT}
          seo_title
          seo_description
          include_apartmentsync
          status
        }
        custom_fields {
          ID
          type
          value
        }
      }
    }
  `;
}

// Legacy export for backward compatibility
export const GetPropertyQuery = ''; // Use buildPropertyQuery instead

export type QueryName = 'GetProperty';

export const queries: Record<QueryName, string> = {
  GetProperty: '',
};
